import React from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Meta from '../components/Meta';
import ResponsiveImage from '../components/ResponsiveImage';
import useLocalePath from '../i18n/useLocalePath';
import posts, { formatPostDate } from '../content/blog';

export default function Blog() {
  const { t } = useTranslation();
  const { isSpanish, lp } = useLocalePath();
  const [params] = useSearchParams();
  const lang = isSpanish ? 'es' : 'en';
  const list = posts[lang];

  /* Soro-era post URLs were /blog?post=<english-slug>. Forward them to the
     post's own page (in this language tree) so old links keep working. */
  const legacySlug = params.get('post');
  if (legacySlug) {
    const en = posts.en.find(p => p.slug === legacySlug);
    const target = en && (isSpanish ? en.pairSlug : en.slug);
    if (target) return <Navigate to={lp(`/blog/${target}`)} replace />;
  }

  return (
    <div className="bg-white">
      <Meta titleKey="blog.meta.title" descKey="blog.meta.description" />

      <div className="max-w-5xl mx-auto px-4 py-12 md:py-16">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center max-w-3xl mx-auto"
        >
          <span className="inline-block rounded-full bg-brand/15 text-[#0a8f8f] font-rubik font-semibold text-small px-3 py-1 mb-4">
            {t('blog.badge')}
          </span>
          <h1 className="font-rubik font-bold text-h2 md:text-h1 text-gray-900 mb-4">
            {t('blog.heading')}
          </h1>
          <p className="font-rubik text-body text-gray-600">
            {t('blog.intro')}
          </p>
        </motion.header>

        {list.length === 0 ? (
          <div className="rounded-2xl border border-brand/30 bg-brand/5 p-8 text-center">
            <span className="text-4xl" role="img" aria-hidden="true">
              🐾
            </span>
            <h2 className="font-rubik font-bold text-h4 text-gray-900 mt-3">
              {t('blog.fallback.title')}
            </h2>
            <p className="font-rubik text-body text-gray-600 mt-2">
              {t('blog.fallback.body')}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:gap-8 sm:grid-cols-2">
            {list.map((post, i) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i, 5) * 0.08 }}
              >
                <Link
                  to={lp(`/blog/${post.slug}`)}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 hover:border-brand/40 hover:shadow-md transition-all"
                >
                  {post.image && (
                    <ResponsiveImage
                      srcBase={post.image}
                      fallbackExt="jpg"
                      alt=""
                      loading={i < 2 ? 'eager' : 'lazy'}
                      decoding="async"
                      className="aspect-video w-full object-cover bg-brand/5"
                    />
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <time
                      className="text-small text-gray-500 font-rubik"
                      dateTime={post.date}
                    >
                      {formatPostDate(post.date, lang)}
                    </time>
                    <h2 className="font-rubik font-semibold text-h5 md:text-h4 text-gray-900 mt-2 group-hover:text-[#0a8f8f] transition-colors">
                      {post.title}
                    </h2>
                    <p className="font-rubik text-bodyS text-gray-600 mt-3 flex-1">
                      {post.description}
                    </p>
                    <span className="inline-block font-rubik font-medium text-[#0a8f8f] text-bodyS mt-4 group-hover:underline">
                      {t('blog.readMore')} →
                    </span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
